import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { setStatus } from 'store/Admin/OrderDisplay';
import { setOrderId, setOrderType } from 'store/Admin/Analyser';

// Hooks & utils
import { ORDERSTATUS } from 'utils/Constants';
import { useWebsiteFilter } from 'utils/hooks/useWebsiteFilter';

// UI Components
import AddIcon from '@mui/icons-material/Add';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import TimelineIcon from '@mui/icons-material/Timeline';
import ListIcon from '@mui/icons-material/List';

import './order.css';
import './InProgressProjects.css';
import { adminClientClientListSelector } from 'store/AdminClient/ClientList';
import { adminClientOrderListSelector } from 'store/AdminClient/OrderList';
import { fetchAdminClientAllOrders } from 'store/AdminClient/OrderList/features';
import {
  fetchadminCLientOrderDisplay,
  fetchadminCLientOrderLineDisplay,
  fetchAdminClientTextOrderDisplay,
} from 'store/AdminClient/OrderDisplay/features';
import ProjectTable from 'components/shared/ProjectTable';
import usePageTracking from 'utils/hooks/usePageTracking';

const ADMIN_CLIENT_PROJECTS_PAGE = '/AdminClient/ProjectsEnCours';

export default function InProgressProjects() {
  usePageTracking(ADMIN_CLIENT_PROJECTS_PAGE);

  const dispatch = useDispatch();
  const history = useHistory();

  const { t } = useTranslation();
  const {
    inProgressOrders = [],
    delayedOrders = [],
    validatedOrders = [],
    nonAttributedOrders = [],
    isLoading,
    isError,
  } = useSelector(adminClientOrderListSelector);

  const { clients, isLoadingClients = false } = useSelector(adminClientClientListSelector) || {};
  const clientsList = Array.isArray(clients) ? clients : [];
  const hasNoWebsiteConfigured =
    clients !== null && !isLoadingClients && clientsList.length === 0;

  const [searchValue, setSearchValue] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState('ALL');
  const [selectedWebsiteUrl] = useWebsiteFilter('AC_Dashboard_SelectedWebsite');

    useEffect(() => {
        document.title = "Typix - Mes projets";
    }, []);

  useEffect(() => {
    window.dispatchEvent(new Event('locationchange'));
    dispatch(fetchAdminClientAllOrders());
  }, [dispatch]);

  useEffect(() => {
    if (hasNoWebsiteConfigured) {
      setIsSearchOpen(false);
      setSearchValue('');
    }
  }, [hasNoWebsiteConfigured]);

  // Fonction pour normaliser les URLs pour la comparaison
  const normalizeUrl = (url) => {
    if (!url) return '';
    return url
      .toString()
      .trim()
      .toLowerCase()
      .replace(/^https?:\/\//, '')
      .replace(/^www\./, '')
      .replace(/\/+$/, '');
  };

  const getCurrentOrders = () => {
    let orders;
    switch (activeFilter) {
      case 'ALL':
        // Combiner tous les projets
        orders = [
          ...nonAttributedOrders,
          ...inProgressOrders,
          ...delayedOrders,
          ...validatedOrders,
        ];
        break;
      case ORDERSTATUS.NONATTRIBUTED:
        orders = nonAttributedOrders;
        break;
      case ORDERSTATUS.INPROGRESS:
        orders = inProgressOrders;
        break;
      case ORDERSTATUS.DELAYED:
        orders = delayedOrders;
        break;
      case ORDERSTATUS.VALID:
        orders = validatedOrders;
        break;
      default:
        orders = inProgressOrders;
    }
    return orders;
  };

  // Filtrer les projets par site web sélectionné
  const filteredOrders = useMemo(() => {
    const orders = getCurrentOrders();
    if (!selectedWebsiteUrl || !orders || orders.length === 0) {
      return (orders || []).filter((order) => {
        const q = (searchValue || '').toString().trim().toLowerCase();
        if (!q) return true;
        const haystack = [
          order?.title,
          order?.keyword,
          order?.url,
          order?.urlPage,
          order?.websiteUrl,
          order?.website?.urlSite,
        ]
          .filter(Boolean)
          .map((v) => v.toString().toLowerCase())
          .join(' ');
        return haystack.includes(q);
      });
    }

    const normalizedSelectedUrl = normalizeUrl(selectedWebsiteUrl);

    const filteredByWebsite = orders.filter((order) => {
      const orderWebsiteUrl = order.websiteUrl || order.website?.urlSite || '';
      const normalizedOrderUrl = normalizeUrl(orderWebsiteUrl);
      return normalizedOrderUrl === normalizedSelectedUrl;
    });

    const q = (searchValue || '').toString().trim().toLowerCase();
    if (!q) return filteredByWebsite;

    return filteredByWebsite.filter((order) => {
      const haystack = [
        order?.title,
        order?.keyword,
        order?.url,
        order?.urlPage,
        order?.websiteUrl,
        order?.website?.urlSite,
      ]
        .filter(Boolean)
        .map((v) => v.toString().toLowerCase())
        .join(' ');
      return haystack.includes(q);
    });
  }, [selectedWebsiteUrl, activeFilter, inProgressOrders, delayedOrders, validatedOrders, nonAttributedOrders, searchValue]);

  const hasNoProjectsAtAll = useMemo(() => {
    if (!selectedWebsiteUrl) {
      return (
        (!inProgressOrders || inProgressOrders.length === 0) &&
        (!delayedOrders || delayedOrders.length === 0) &&
        (!validatedOrders || validatedOrders.length === 0) &&
        (!nonAttributedOrders || nonAttributedOrders.length === 0)
      );
    }

    const normalizedSelectedUrl = normalizeUrl(selectedWebsiteUrl);

    const filterByWebsite = (orders) => {
      if (!Array.isArray(orders) || orders.length === 0) return [];
      return orders.filter((order) => {
        const orderWebsiteUrl = order.websiteUrl || order.website?.urlSite || '';
        const normalizedOrderUrl = normalizeUrl(orderWebsiteUrl);
        return normalizedOrderUrl === normalizedSelectedUrl;
      });
    };

    const filteredInProgress = filterByWebsite(inProgressOrders);
    const filteredDelayed = filterByWebsite(delayedOrders);
    const filteredValidated = filterByWebsite(validatedOrders);
    const filteredNonAttributed = filterByWebsite(nonAttributedOrders);


    return (
      filteredInProgress.length === 0 &&
      filteredDelayed.length === 0 &&
      filteredValidated.length === 0 &&
      filteredNonAttributed.length === 0
    );
  }, [selectedWebsiteUrl, inProgressOrders, delayedOrders, validatedOrders, nonAttributedOrders]);

  const handleFilterChange = (status) => {
    setActiveFilter(status);
  };

  const handleGoToAddWebsite = () => {
    history.push('/AdminClient/AjouterSiteWeb');
  };

  return (
    <div>

      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 0 20px 0',
        borderBottom: '1px solid #e6e4e4',
        marginBottom: '20px'
      }}>
        {/* Titre et filtres */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px', flex: 1 }}>
          <h2 style={{
            margin: 0,
            fontSize: '24px',
            fontWeight: 600,
            color: '#3c3c3c',
            marginRight: '20px'
          }}>
            {t('Projets')}
          </h2>

          {/* Boutons de filtre */}
          <div style={{ display: 'flex', gap: '8px' }}>
            {/* Nouveau bouton "Tous" */}
            <button
              onClick={() => handleFilterChange('ALL')}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                border: '1px solid #e0e0e0',
                backgroundColor: activeFilter === 'ALL' ? '#f5f5f5' : '#ffffff',
                color: '#3c3c3c',
                fontSize: '14px',
                fontWeight: 400,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontFamily: 'Poppins, sans-serif',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                if (activeFilter !== 'ALL') {
                  e.currentTarget.style.backgroundColor = '#f9f9f9';
                }
              }}
              onMouseLeave={(e) => {
                if (activeFilter !== 'ALL') {
                  e.currentTarget.style.backgroundColor = '#ffffff';
                }
              }}
            >
              <ListIcon style={{ fontSize: '16px' }} />
              {t('Tous')}
            </button>
            
            <button
              onClick={() => handleFilterChange(ORDERSTATUS.NONATTRIBUTED)}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                border: '1px solid #e0e0e0',
                backgroundColor: activeFilter === ORDERSTATUS.NONATTRIBUTED ? '#f5f5f5' : '#ffffff',
                color: '#3c3c3c',
                fontSize: '14px',
                fontWeight: 400,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontFamily: 'Poppins, sans-serif',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                if (activeFilter !== ORDERSTATUS.NONATTRIBUTED) {
                  e.currentTarget.style.backgroundColor = '#f9f9f9';
                }
              }}
              onMouseLeave={(e) => {
                if (activeFilter !== ORDERSTATUS.NONATTRIBUTED) {
                  e.currentTarget.style.backgroundColor = '#ffffff';
                }
              }}
            >
              <TimelineIcon style={{ fontSize: '16px' }} />
              {t('Non attribués')}
            </button>
            <button
              onClick={() => handleFilterChange(ORDERSTATUS.INPROGRESS)}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                border: '1px solid #e0e0e0',
                backgroundColor: activeFilter === ORDERSTATUS.INPROGRESS ? '#f5f5f5' : '#ffffff',
                color: '#3c3c3c',
                fontSize: '14px',
                fontWeight: 400,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontFamily: 'Poppins, sans-serif',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                if (activeFilter !== ORDERSTATUS.INPROGRESS) {
                  e.currentTarget.style.backgroundColor = '#f9f9f9';
                }
              }}
              onMouseLeave={(e) => {
                if (activeFilter !== ORDERSTATUS.INPROGRESS) {
                  e.currentTarget.style.backgroundColor = '#ffffff';
                }
              }}
            >
              <RadioButtonUncheckedIcon style={{ fontSize: '16px' }} />
              {t('En cours')}
            </button>
            <button
              onClick={() => handleFilterChange(ORDERSTATUS.DELAYED)}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                border: '1px solid #e0e0e0',
                backgroundColor: activeFilter === ORDERSTATUS.DELAYED ? '#f5f5f5' : '#ffffff',
                color: '#3c3c3c',
                fontSize: '14px',
                fontWeight: 400,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontFamily: 'Poppins, sans-serif',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                if (activeFilter !== ORDERSTATUS.DELAYED) {
                  e.currentTarget.style.backgroundColor = '#f9f9f9';
                }
              }}
              onMouseLeave={(e) => {
                if (activeFilter !== ORDERSTATUS.DELAYED) {
                  e.currentTarget.style.backgroundColor = '#ffffff';
                }
              }}
            >
              <AccessTimeIcon style={{ fontSize: '16px' }} />
              {t('En retard')}
            </button>
            <button
              onClick={() => handleFilterChange(ORDERSTATUS.VALID)}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                border: '1px solid #e0e0e0',
                backgroundColor: activeFilter === ORDERSTATUS.VALID ? '#f5f5f5' : '#ffffff',
                color: '#3c3c3c',
                fontSize: '14px',
                fontWeight: 400,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontFamily: 'Poppins, sans-serif',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                if (activeFilter !== ORDERSTATUS.VALID) {
                  e.currentTarget.style.backgroundColor = '#f9f9f9';
                }
              }}
              onMouseLeave={(e) => {
                if (activeFilter !== ORDERSTATUS.VALID) {
                  e.currentTarget.style.backgroundColor = '#ffffff';
                }
              }}
            >
              <CheckCircleOutlineIcon style={{ fontSize: '16px' }} />
              {t('Validés')}
            </button>
          </div>
        </div>

        {/* Bouton Ajouter un projet */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button
            type="button"
            disabled={hasNoWebsiteConfigured}
            aria-disabled={hasNoWebsiteConfigured}
            onClick={() => {
              if (hasNoWebsiteConfigured) return;
              history.push('/AdminClient/CreateProject');
            }}
            style={{
              padding: '10px 20px',
              borderRadius: '8px',
              border: 'none',
              backgroundColor: '#6458F1',
              color: '#ffffff',
              fontSize: '14px',
              fontWeight: 500,
              cursor: hasNoWebsiteConfigured ? 'not-allowed' : 'pointer',
              opacity: hasNoWebsiteConfigured ? 0.55 : 1,
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontFamily: 'Poppins, sans-serif',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              if (e.currentTarget.disabled) return;
              e.currentTarget.style.backgroundColor = '#5548d1';
              e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.15)';
            }}
            onMouseLeave={(e) => {
              if (e.currentTarget.disabled) return;
              e.currentTarget.style.backgroundColor = '#6458F1';
              e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
            }}
          >
            <AddIcon style={{ fontSize: '18px' }} />
            {t('Ajouter un projet')}
          </button>
        </div>
      </div>

      {hasNoWebsiteConfigured && (
        <div className="projects-encours-help-banner" role="note">
          <div className="projects-encours-help-banner-left">
            <span className="projects-encours-help-banner-text">
              {t('Aucun projet disponible — veuillez d’abord ajouter un siteweb.')}
            </span>
          </div>
          <div className="projects-encours-help-banner-actions">
            <button
              type="button"
              onClick={handleGoToAddWebsite}
              className="projects-encours-help-banner-link"
            >
              {t('Ajouter un siteweb')}
            </button>
          </div>
        </div>
      )}


      {!hasNoWebsiteConfigured && (
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 12,
          marginBottom: 12,
        }}
      >
        {!isSearchOpen ? (
          <img
            src="/Images/search-icon.png"
            alt="search"
            onClick={() => setIsSearchOpen(true)}
            style={{
              width: 32,
              height: 32,
              cursor: 'pointer',
            }}
          />
        ) : (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              maxWidth: 320,
              width: '100%',
            }}
          >
            <div
              style={{
                position: 'relative',
                flex: 1,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <img
                src="/Images/search-icon2.png"
                alt="search"
                style={{
                  position: 'absolute',
                  left: 10,
                  width: 16,
                  height: 16,
                  pointerEvents: 'none',
                }}
              />
              <input
                type="text"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                placeholder={t('Rechercher')}
                style={{
                  width: '100%',
                  padding: '8px 36px 8px 34px',
                  border: '1px solid #E5E7EB',
                  borderRadius: 6,
                  fontSize: 14,
                  outline: 'none',
                }}
              />
            </div>
            <button
              type="button"
              onClick={() => {
                setIsSearchOpen(false);
                setSearchValue('');
              }}
              style={{
                background: '#F9FAFB',
                border: '1px solid #E5E7EB',
                borderRadius: 6,
                padding: '6px 10px',
                cursor: 'pointer',
                fontSize: 13,
                color: '#111827',
              }}
            >
              {t('Fermer')}
            </button>
          </div>
        )}
      </div>
      )}

      <ProjectTable
        orderStatus={activeFilter}
        orders={filteredOrders}
        isLoading={isLoading}
        isError={isError}
        hasNoProjectsAtAll={hasNoProjectsAtAll}
        disableEmptyStateCreateProject={hasNoWebsiteConfigured}
        fetchDisplayData={(id, orderType) => {
          dispatch(setStatus(activeFilter));
          dispatch(setOrderId(id));
          dispatch(setOrderType(orderType));
          dispatch(fetchadminCLientOrderDisplay(id));
          dispatch(fetchAdminClientTextOrderDisplay(id));
          dispatch(fetchadminCLientOrderLineDisplay(id));
        }}
        onRowClickRedirectPath="/AdminClient/DetailsProject"
        hideTitle={true}
        hideSearch={true}
        hidePaginationSelector={true}
        disableWebsiteFilter={true}
      />
    </div>
  );
}